/**
 * CustomDragLayer.jsx
 *
 * Renders a floating preview of a TaskColumnsEditorItem that follows the cursor while being dragged.
 *
 * This component uses react-dnd's useDragLayer hook to subscribe to the global drag state.
 * It renders a non-interactive visual copy of the dragged item that follows the cursor.
 *
 * Features:
 * - Matches the width of the container element referenced by containerRef, or falls back to a default width if unavailable.
 * - Uses theme-based styling for visual consistency
 * - Accurately positions the preview relavive to the user's pointer position using calculated offsets.
 * - Non-interactive and sits above other content without capturing events
 */

import PropTypes from "prop-types";
import React from "react";
import { useDragLayer } from "react-dnd";
import { useTheme } from "@mui/material/styles";
import TaskColumnsEditorItem, { ItemTypes } from './TaskColumnsEditorItem';

const DEFAULT_WIDTH = 347; // Used when containerRef is not available or not yet measured

const layerStyles = {
  // Fixed-position overlay that covers the entire screen during drag
  position: "fixed",
  pointerEvents: "none", // Allow interactions to pass through
  zIndex: 100,           // Ensure the layer appears above other elements
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

/**
 * Returns the width of the container element to size the drag preview.
 * Falls back to a default width if the container reference is unavailable.
 *
 * @param {React.RefObject} ref - Ref to the container element
 * @returns {number} Width in pixels
 */
function getPreviewWidth(ref) {
  return ref?.current?.offsetWidth || DEFAULT_WIDTH;
}

/**
 * CustomDragLayer component
 *
 * Displays a draggable preview of a TaskColumnsEditorItem at the current pointer position.
 *
 * @param {Object} props
 * @param {Array} props.columns - List of all available columns
 * @param {React.RefObject} props.containerRef - Ref to the container to determine item width
 * @param {Function} props.onChange - Handler for checkbox toggle (noop in preview)
 * @param {Function} props.onMove - Handler for reordering (noop in preview)
 * @returns {React.Element|null}
 */
const CustomDragLayer = ({
  columns,
  containerRef,
  onChange,
  onMove,
}) => {
  const theme = useTheme();

  // Extract drag state from react-dnd
  const {
    currentOffset,
    initialOffset,
    initialClientOffset,
    isDragging,
    item,
    itemType,
  } = useDragLayer((monitor) => ({
    currentOffset: monitor.getClientOffset(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    initialClientOffset: monitor.getInitialClientOffset(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
  }));

  // Render the preview only while a column item is being dragged.
  if (!isDragging || itemType !== ItemTypes.COLUMN) {
    return null;
  }

  if (!initialOffset || !currentOffset || !initialClientOffset) {
    return null;
  }

  const dragPreviewOffsetX = initialClientOffset.x - initialOffset.x;
  const dragPreviewOffsetY = initialClientOffset.y - initialOffset.y;

  // Look up the dragged column data using its index
  const draggedColumn = columns?.[item.index];
  if (!draggedColumn) return null;

  // Determine the width for the drag preview (match container or fallback)
  const width = getPreviewWidth(containerRef);

  function getItemStyles() {
    const { x, y } = currentOffset;
    return {
      backgroundColor: theme.palette.action.hover,
      transform: `translate(${x - dragPreviewOffsetX}px, ${y - dragPreviewOffsetY}px)`,
      width: `${width}px`,
      position: "fixed",
      pointerEvents: "none",
    };
  }

  return (
    <div style={layerStyles}>
      <div style={getItemStyles()}>
        <TaskColumnsEditorItem
          column={draggedColumn}
          disabled // Disable interactivity in the preview
          index={item.index}
          onChange={onChange}
          onMove={onMove}
        />
      </div>
    </div>
  );
};

CustomDragLayer.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  containerRef: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
};

export default CustomDragLayer;
